import { lazy } from 'react';

import { userManager } from '@/shared/authentication/services/oidc';

import { lazyRoute } from './lazy-route';

import type { RouteObject } from 'react-router-dom';

// / — index route: no path, so it stays outside the lazyRoute factory.
const Home = lazy(() => import('@/modules/HomeModule'));
const home: RouteObject = {
  index: true,
  element: <Home />
};

const idpInitiatedLogin: RouteObject = {
  path: '/login/idpInitiated',
  loader: async () => {
    await userManager.signinRedirect().catch((error: unknown) => {
      throw new Response(`Something went wrong while redirecting to login page: ${String(error)}`);
    });
  }
};

// /login
const login: RouteObject = {
  path: '/login',
  loader: async () => {
    const params = new URLSearchParams(window.location.search);
    const access_token = params.get('access_token') ?? '';
    return userManager
      .signinRedirect({
        extraQueryParams: {
          access_token
        }
      })
      .catch((error: unknown) => {
        throw new Response(
          `Something went wrong while redirecting to login page: '${String(error)}`
        );
      });
  }
};

// /operationRedirect
const operationRedirect = lazyRoute(
  '/operationRedirect',
  () => import('@/containers/DefaultOperationRedirectContainer/DefaultOperationRedirectContainer')
);

// /callback
const callback = lazyRoute('/callback/:type', () => import('@/callback'));

// /operations
const operations = lazyRoute('/operations', () => import('@/modules/OperationViewModule'));

// /account
const account = lazyRoute('/account', () =>
  import('@/features/account-preferences').then(m => ({ default: m.AccountPreferences }))
);

// /user-agreement
const userAgreement = lazyRoute(
  'user-agreement',
  () => import('@/components/UserAgreement/UserAgreement')
);

// /user-agreement-form
const userAgreementForm = lazyRoute('user-agreement-form', () =>
  import('@/features/user-agreement-form').then(m => ({ default: m.UserAgreementForm }))
);

// One component serves every unsubscribe entry point; the paths are the only
// difference (the newsletter one is absolute — kept verbatim).
const unsubscribeRoutes = [
  '/unsubscribe/newsletter',
  'unsubscribe/alert',
  'unsubscribe/alert/lrp',
  'unsubscribe/endorsement',
  'unsubscribe/zoomCall'
].map(path =>
  lazyRoute(path, () => import('@/components/UnsubscribeNewsletter/UnsubscribeNewsletter'))
);

// /whats-new
const WhatsNew = lazy(() => import('@/features/whats-new/pages/WhatsNewPage'));
const NotificationDetailPage = lazy(
  () => import('@/features/whats-new/pages/NotificationDetailPage')
);
export const whatsNew: RouteObject = {
  path: 'whats-new',
  children: [
    { index: true, element: <WhatsNew /> },
    { path: ':slug', element: <NotificationDetailPage /> }
  ]
};

// 404
const fourOFour = lazyRoute('*', () => import('@/modules/FourOFour'));

export const miscRoutes: RouteObject[] = [
  home,
  idpInitiatedLogin,
  login,
  operationRedirect,
  callback,
  operations,
  account,
  userAgreement,
  userAgreementForm,
  ...unsubscribeRoutes,
  whatsNew,
  fourOFour
];
